import { Integrations } from 'constants/integrations';
import { podTicketUrl } from 'components/CallScribe/podTicketUrl';

export const POST_TO_TICKET_PROMPT =
  'This call was placed from a contact record. Enter a ticket number to post the time entry to it.';

export const NO_TICKET_PROMPT = 'This call isn\'t linked to a ticket. Enter a ticket number to post time.';

export const ticketPromptFor = (fromContactRecord: boolean): string =>
  (fromContactRecord ? POST_TO_TICKET_PROMPT : NO_TICKET_PROMPT);

export const TICKET_NUMBER_REQUIRED = 'Enter the ticket number this call belongs to.';

export const psaLabel = (psa: Integrations | undefined): string => {
  switch (psa) {
    case Integrations.Halo:
      return 'HaloPSA';
    case Integrations.Autotask:
      return 'Autotask';
    case Integrations.ConnectWise:
    default:
      return 'ConnectWise';
  }
};

export const ticketUnconfirmedMessage = (
  ticketId: string,
  psa: Integrations | undefined,
): string =>
  `Could not confirm ticket #${ticketId} with ${psaLabel(psa)}. Check the number and try again.`;

export const alreadyLinkedMessage = (ticketId: string): string =>
  `This call already belongs to ticket #${ticketId}.`;

export const isUsableTicketNumber = (value: string): boolean => /^\d+$/.test(value.trim());

export interface ICheckedTicket {
  ticketId: string;
  ticketNumber: string;
  contactName: string;
}

export type TicketCheckOutcome =
  | { status: 'found'; ticket: ICheckedTicket }
  | { status: 'unconfirmed'; message: string };

const readTicket = (
  data: Record<string, unknown> | null | undefined,
  ticketId: string,
): ICheckedTicket => ({
  ticketId,
  ticketNumber: String(
    (data?.TicketNumber as string | undefined) ??
      (data?.ticketNumber as string | undefined) ??
      ticketId,
  ),
  contactName: String(
    (data?.ContactName as string | undefined) ??
      (data?.contactName as string | undefined) ??
      '',
  ),
});

export const checkedTicketLabel = (ticket: ICheckedTicket): string =>
  (ticket.contactName
    ? `#${ticket.ticketNumber} — ${ticket.contactName}`
    : `#${ticket.ticketNumber}`);

export interface ITicketCheckRequest {
  psa: Integrations | undefined;
  psaId: number;
  ticketId: string;
  fetchJson: (url: string) => Promise<{ status: number; data: unknown }>;
}

export const checkTicket = async (
  request: ITicketCheckRequest,
): Promise<TicketCheckOutcome> => {
  const { psa, psaId, ticketId, fetchJson } = request;
  const url = podTicketUrl(psa, psaId, ticketId);
  if (!url) {
    return { status: 'unconfirmed', message: ticketUnconfirmedMessage(ticketId, psa) };
  }

  try {
    const res = await fetchJson(url);
    if (res.status >= 200 && res.status < 300 && res.data) {
      return {
        status: 'found',
        ticket: readTicket(res.data as Record<string, unknown>, ticketId),
      };
    }
    return { status: 'unconfirmed', message: ticketUnconfirmedMessage(ticketId, psa) };
  } catch {
    return { status: 'unconfirmed', message: ticketUnconfirmedMessage(ticketId, psa) };
  }
};
